/**
 * @author NikolaV
 */

import * as React from 'react';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { Button } from '@shared/ui-react/Button';
import * as ReactDOM from 'react-dom';
import { FilterSelectionFeedback } from '@shared/map/FilterSelectionFeedback';
import './MapPanelFilter.scss';
import { makeObservable, observable } from 'mobx';
import { observer } from 'mobx-react';

@observer
export class MapPanelFilter extends React.Component<{
    opened?: boolean;
    hidden?: boolean;
    toggleOpened?: () => void;
    pinned: boolean;
    togglePinned: () => void;
    pinLeftOffset: number;
    activeFiltersCount: number;
    clearFilter: () => void;
    // when panels are not unmounted but just hidden (display: none) we need to detect visibility change and hide the pinned filter panel
    detectVisibilityChange?: boolean;
    children?: React.ReactNode;
}> {
    button: Button;
    panel: HTMLDivElement;
    wrapper: HTMLSpanElement;
    intersectionObserver: IntersectionObserver = null;
    @observable isVisibleOnScreen = false;

    static defaultProps = {
        detectVisibilityChange: true,
    };

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidUpdate(prevProps: Readonly<{ opened?: boolean; toggleOpened?: () => void; pinned: boolean; togglePinned: () => void }>, prevState: Readonly<{}>, snapshot?: any) {
        this.reposition();
    }

    componentDidMount() {
        this.reposition();

        if (this.props.detectVisibilityChange) {
            setTimeout(() => {
                // when pinned filter button is not visible but is in dom, also hide panel
                this.intersectionObserver = new IntersectionObserver(entries => (this.isVisibleOnScreen = entries[0].isIntersecting));

                if (this.wrapper) {
                    this.intersectionObserver.observe(this.wrapper);
                }
            }, 1000);
        } else {
            this.isVisibleOnScreen = true;
        }
    }

    componentWillUnmount() {
        if (this.intersectionObserver) {
            this.intersectionObserver.unobserve(this.wrapper);
        }
    }

    reposition() {
        if (this.props.pinned && this.props.opened && this.panel && this.button) {
            this.panel.style.left = this.props.pinLeftOffset + 'px';
            this.panel.style.top = this.button.el.getBoundingClientRect().top + 'px';
        }
    }

    render() {
        const Badge = <div className='filter-badge' />;

        const Panel = (
            <>
                <div className='padded-half flex-row ai-center'>
                    <span>
                        <FilterSelectionFeedback filtersCount={this.props.activeFiltersCount} onClearFilters={this.props.clearFilter} />
                    </span>
                    {this.props.togglePinned && (
                        <Button
                            secondary
                            subtle
                            className='ik-button-small ik-button-secondary--subtle'
                            icon='iki iki-pin'
                            style={{ fontSize: '1.2em', padding: '3px 6px', marginLeft: 'auto', color: this.props.pinned ? '#4797cc' : null }}
                            onClick={this.props.togglePinned}
                        />
                    )}
                </div>
                <main>{this.props.children}</main>
            </>
        );

        const BtnInside = (
            <span className='flex-row ai-center' style={{ padding: '3px 2px' }}>
                {this.props.activeFiltersCount ? Badge : null}
                <i className='iki iki-filter' style={{ fontSize: '1.2rem' }} />
            </span>
        );

        if (!this.props.pinned) {
            return (
                <span ref={el => (this.wrapper = el)}>
                    <Dropdown
                        isOpen={this.props.opened}
                        onToggle={this.props.toggleOpened}
                        buttonStyle={{ marginLeft: 'auto' }}
                        className='ik-button-secondary--subtle ik-button-small'
                        tooltip={__('general.filters')}
                    >
                        <>{BtnInside}</>
                        <>{Panel}</>
                    </Dropdown>
                </span>
            );
        }
        return (
            <>
                <span ref={el => (this.wrapper = el)}>
                    <Button secondary subtle ref={el => (this.button = el)} className='ik-button-small' onClick={this.props.toggleOpened} tooltip={__('general.filters')}>
                        {BtnInside}
                    </Button>
                </span>
                {!this.isVisibleOnScreen || !this.props.opened || this.props.hidden || !this.props.pinLeftOffset
                    ? null
                    : ReactDOM.createPortal(
                          <div ref={el => (this.panel = el)} className='card' style={{ position: 'fixed', zIndex: 9100 }}>
                              {Panel}
                          </div>,
                          document.body
                      )}
            </>
        );
    }
}
